import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Building2, Package, Heart, MessageSquare, Edit2, Check, X, Trash2, Tag } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import api from '../lib/api';
import FavoriteListsPopover, { FavoriteListBadges } from './FavoriteListsPopover';
import resolveProduct from '../domain/product/resolveProduct';

/**
 * FavoritesProductsTable - Tableau des favoris Retailer
 * 
 * Composant tableau responsive pour afficher les favoris avec:
 * - Visuel produit
 * - Désignation produit
 * - Distributeur
 * - Listes (badges + popover multi-sélection)
 * - Commentaire éditable inline
 * 
 * Props:
 * @param {Array} favorites - Liste des favoris (avec product enrichi)
 * @param {Array} allLists - Toutes les listes de favoris de l'utilisateur
 * @param {boolean} loading - État de chargement
 * @param {Function} onCommentUpdate - Callback après mise à jour commentaire
 * @param {Function} onRemoveFavorite - Callback pour supprimer un favori
 * @param {Function} onListsUpdate - Callback après modification des listes d'un favori
 */
const FavoritesProductsTable = ({ 
  favorites = [], 
  allLists = [],
  loading = false,
  onCommentUpdate = null,
  onRemoveFavorite = null,
  onListsUpdate = null
}) => {
  
  // État pour l'édition inline du commentaire
  const [editingId, setEditingId] = useState(null);
  const [editingComment, setEditingComment] = useState('');
  const [savingComment, setSavingComment] = useState(false);

  // Obtenir l'image du produit
  const getProductImage = (product) => {
    if (product?.visuels && product.visuels.length > 0) {
      return product.visuels[0];
    }
    if (product?.images && product.images.length > 0) {
      return product.images[0];
    }
    return null;
  };

  // Démarrer l'édition d'un commentaire
  const startEditing = useCallback((fav) => {
    setEditingId(fav.id);
    setEditingComment(fav.commentaire || '');
  }, []);

  // Annuler l'édition
  const cancelEditing = useCallback(() => {
    setEditingId(null);
    setEditingComment('');
  }, []);

  // Sauvegarder le commentaire via product_comments (source de vérité V2)
  const saveComment = useCallback(async (favoriteId) => {
    setSavingComment(true);
    try {
      // Trouver le product_id du favori
      const fav = favorites.find(f => f.id === favoriteId);
      const productId = fav?.product_id;
      if (!productId) throw new Error('Product ID not found');
      
      await api.put('/product-comments', {
        product_id: productId,
        comment: editingComment.trim() || null
      });
      
      toast.success('Commentaire mis à jour');
      
      if (onCommentUpdate) {
        onCommentUpdate(favoriteId, editingComment.trim() || null);
      }
      
      setEditingId(null);
      setEditingComment('');
    } catch (error) {
      console.error('Error saving comment:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingComment(false);
    }
  }, [editingComment, onCommentUpdate, favorites]);

  // Supprimer un favori
  const handleRemove = useCallback(async (favoriteId, productId) => {
    if (onRemoveFavorite) {
      onRemoveFavorite(favoriteId, productId);
    }
  }, [onRemoveFavorite]);

  // Mise à jour des listes d'un favori
  const handleListsChange = useCallback((favoriteId, newListIds) => {
    if (onListsUpdate) {
      onListsUpdate(favoriteId, newListIds);
    }
  }, [onListsUpdate]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border overflow-hidden">
      {/* Desktop/Tablet Table View */}
      <div className="hidden sm:block overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="w-[80px]">Visuel</TableHead>
              <TableHead className="min-w-[180px]">Désignation</TableHead>
              <TableHead className="min-w-[130px]">Distributeur</TableHead>
              <TableHead className="min-w-[150px]">Listes</TableHead>
              <TableHead className="min-w-[200px]">Commentaire</TableHead>
              <TableHead className="w-[80px] text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {favorites.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                  <Heart className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Aucun produit dans cette sélection de la Wishlist</p>
                </TableCell>
              </TableRow>
            ) : (
              favorites.map((fav) => {
                const product = fav.product || {};
                const imageUrl = getProductImage(product) || fav.product_image;
                const isEditing = editingId === fav.id;
                const listIds = fav.list_ids || [];
                
                return (
                  <TableRow 
                    key={fav.id} 
                    className="hover:bg-gray-50 transition-colors"
                    data-testid={`favorite-row-${fav.id}`}
                  >
                    {/* Visuel */}
                    <TableCell className="p-2">
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                        {imageUrl ? (
                          <img 
                            src={imageUrl} 
                            alt={product.intitule || fav.product_intitule}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                            }}
                          />
                        ) : null}
                        <div 
                          className={`w-full h-full items-center justify-center ${imageUrl ? 'hidden' : 'flex'}`}
                        >
                          <Package className="h-6 w-6 text-gray-400" />
                        </div>
                      </div>
                    </TableCell>
                    
                    {/* Désignation */}
                    <TableCell>
                      <Link 
                        to={`/dashboard/produit/${fav.product_id}`}
                        className="group flex flex-col gap-1"
                      >
                        <span className="font-medium text-gray-900 group-hover:text-primary transition-colors line-clamp-2">
                          {product.intitule || fav.product_intitule || 'Produit'}
                        </span>
                        {(() => {
                          const pubDisplay = (product.original_publishers || product.publishers || []).map(p => p.name).filter(Boolean).join(' / ') || fav.product_editeur_display || '';
                          return pubDisplay ? (
                            <span className="text-xs text-muted-foreground">{pubDisplay}</span>
                          ) : null;
                        })()}
                        {(() => { const p = resolveProduct(product).pricing.price ?? fav.product_prix; return p ? (
                          <span className="text-sm font-semibold text-primary">
                            {p.toFixed(2)} €
                          </span>
                        ) : null; })()}
                      </Link>
                    </TableCell>
                    
                    {/* Distributeur */}
                    <TableCell>
                      {product.partner_name ? (
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
                          <span className="text-sm">{product.partner_name}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    
                    {/* Listes */}
                    <TableCell>
                      <div className="flex flex-col gap-2">
                        {/* Badges des listes associées */}
                        <FavoriteListBadges 
                          listIds={listIds} 
                          allLists={allLists} 
                          maxDisplay={2}
                        />
                        
                        {/* Popover pour ajouter/retirer des listes */}
                        <FavoriteListsPopover
                          favoriteId={fav.id}
                          currentListIds={listIds}
                          allLists={allLists}
                          onListsChange={handleListsChange}
                          isMobile={false}
                        />
                      </div>
                    </TableCell>
                    
                    {/* Commentaire */}
                    <TableCell>
                      {isEditing ? (
                        <div className="flex flex-col gap-2">
                          <Textarea
                            value={editingComment}
                            onChange={(e) => setEditingComment(e.target.value)}
                            placeholder="Votre commentaire personnel..."
                            className="min-h-[60px] text-sm resize-none"
                            maxLength={500}
                            autoFocus
                            data-testid={`comment-textarea-${fav.id}`}
                          />
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => saveComment(fav.id)}
                              disabled={savingComment}
                              data-testid={`save-comment-${fav.id}`}
                            >
                              <Check className="h-3 w-3 mr-1" />
                              {savingComment ? '...' : 'OK'}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={cancelEditing}
                              disabled={savingComment}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                            <span className="text-xs text-muted-foreground ml-auto">
                              {editingComment.length}/500
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div 
                          className="group cursor-pointer p-2 -m-2 rounded hover:bg-gray-100 transition-colors min-h-[40px] flex items-start gap-2"
                          onClick={() => startEditing(fav)}
                          data-testid={`comment-display-${fav.id}`}
                        >
                          {fav.commentaire ? (
                            <p className="text-sm text-gray-700 line-clamp-3 flex-1">
                              {fav.commentaire}
                            </p>
                          ) : (
                            <p className="text-sm text-muted-foreground italic flex-1">
                              Ajouter un commentaire...
                            </p>
                          )}
                          <Edit2 className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1" />
                        </div>
                      )}
                    </TableCell>
                    
                    {/* Actions */}
                    <TableCell className="text-center">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleRemove(fav.id, fav.product_id)}
                        data-testid={`remove-favorite-${fav.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Card View */}
      <div className="sm:hidden divide-y">
        {favorites.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <Heart className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Aucun produit dans cette sélection de la Wishlist</p>
          </div>
        ) : (
          favorites.map((fav) => {
            const product = fav.product || {};
            const imageUrl = getProductImage(product) || fav.product_image;
            const isEditing = editingId === fav.id;
            const listIds = fav.list_ids || [];
            
            return (
              <div 
                key={fav.id}
                className="p-4 hover:bg-gray-50 transition-colors"
                data-testid={`favorite-card-mobile-${fav.id}`}
              >
                <div className="flex gap-4">
                  {/* Visuel */}
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
                    {imageUrl ? (
                      <img 
                        src={imageUrl} 
                        alt={product.intitule || fav.product_intitule}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <Package className="h-8 w-8 text-gray-400" />
                    )}
                  </div>
                  
                  {/* Infos */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <Link 
                        to={`/dashboard/produit/${fav.product_id}`}
                        className="font-medium text-gray-900 hover:text-primary transition-colors line-clamp-2 flex-1"
                      >
                        {product.intitule || fav.product_intitule || 'Produit'}
                      </Link>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0 -mr-2 -mt-1"
                        onClick={() => handleRemove(fav.id, fav.product_id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    {/* Distributeur */}
                    {product.partner_name && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
                        <Building2 className="h-3 w-3 text-blue-500" />
                        {product.partner_name}
                      </div>
                    )}
                    
                    {/* Prix */}
                    {(() => { const p = resolveProduct(product).pricing.price ?? fav.product_prix; return p ? (
                      <Badge variant="secondary" className="text-xs mb-2">
                        {p.toFixed(2)} €
                      </Badge>
                    ) : null; })()}
                  </div>
                </div>
                
                {/* Listes - Mobile */}
                <div className="mt-3 pt-3 border-t">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Tag className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <FavoriteListBadges 
                          listIds={listIds} 
                          allLists={allLists} 
                          maxDisplay={3}
                        />
                        {listIds.length === 0 && (
                          <span className="text-sm text-muted-foreground italic">
                            Aucune liste
                          </span>
                        )}
                      </div>
                    </div>
                    <FavoriteListsPopover
                      favoriteId={fav.id}
                      currentListIds={listIds}
                      allLists={allLists}
                      onListsChange={handleListsChange}
                      isMobile={true}
                    />
                  </div>
                </div>
                
                {/* Commentaire - Mobile */}
                <div className="mt-3 pt-3 border-t">
                  {isEditing ? (
                    <div className="flex flex-col gap-2">
                      <Textarea
                        value={editingComment}
                        onChange={(e) => setEditingComment(e.target.value)}
                        placeholder="Votre commentaire personnel..."
                        className="min-h-[60px] text-sm resize-none"
                        maxLength={500}
                        autoFocus
                      />
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => saveComment(fav.id)}
                          disabled={savingComment}
                        >
                          <Check className="h-3 w-3 mr-1" />
                          {savingComment ? '...' : 'Enregistrer'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={cancelEditing}
                          disabled={savingComment}
                        >
                          Annuler
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="flex items-start gap-2 cursor-pointer p-2 -m-2 rounded hover:bg-gray-100"
                      onClick={() => startEditing(fav)}
                    >
                      <MessageSquare className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                      {fav.commentaire ? (
                        <p className="text-sm text-gray-700 flex-1">
                          {fav.commentaire}
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground italic flex-1">
                          Ajouter un commentaire...
                        </p>
                      )}
                      <Edit2 className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default FavoritesProductsTable;
